/* @flow */

import ObjectID from 'bson-objectid';
import Promise from 'bluebird';
import dayjs from 'dayjs';
import { Manager } from 'react-as-promised';
import {
  omitBy, omit, isUndefined,
} from 'lodash/fp';
import { batch } from 'react-redux';

import useActionCreator from '../../hooks/useActionCreator';
import l from '../../utils/localization';
import { serializeTodoItem } from './transform';
import lysterClient from '../../clients/lyster-client';
import {
  authorizationHeaderSelector,
  isLoggedInSelector,
} from '../auth/selectors';
import {
  nextStatusChangeSelector,
} from './selectors';
import {
  isConnectedSelector,
} from '../system/selectors';
import { recurringTextDescription } from '../../utils/text-helper';
import { logEvent } from '../../utils/analytics';
import bugsnagClient from '../../clients/bugsnag-client';
import waitForUndo from '../../observables/waitForUndo';
import { updateSetting } from '../settings/actions';


const omitUndefined = omitBy(isUndefined);

let countdownToNextChangeTimer = null;

export const scheduleNextTodoChange = (): ThunkAction => (
  dispatch,
  getState,
) => {
  const state = getState(),
        now = new Date(),
        nextStatusChange = nextStatusChangeSelector(state);

  if (countdownToNextChangeTimer) {
    countdownToNextChangeTimer.cancel();
  }

  if (
    nextStatusChange && state.todo.nextStatusChange
      ? nextStatusChange.valueOf() !== state.todo.nextStatusChange.valueOf()
      : nextStatusChange !== state.todo.nextStatusChange
  ) {
    dispatch({
      type: 'TODO_UPDATE_NEXT_STATUS_CHANGE',
      payload: nextStatusChange,
    });
  }

  if (nextStatusChange) {
    const duration = nextStatusChange - now;
    if (duration < 120000) {
      // best-effort (Android timers will cancel etc)
      countdownToNextChangeTimer = Promise.delay(duration).then(
        () => dispatch(scheduleNextTodoChange()),
      );
    }
  }
  return null;
};

export const forceSync = (): ThunkAction => (dispatch, getState) => {
  const state = getState();

  if (isLoggedInSelector(state) && isConnectedSelector(state)) {
    waitForUndo.set(false);
    dispatch({ type: 'CLEAR_UNDO' });

    dispatch({ type: 'Offline/COMPLETE_RETRY' });
  }
};

export const getAllTodos = (isFetchRequestedByUser = false): Promise<any> => (
  dispatch: Dispatch,
  getState: () => AppState,
) => {
  const state = getState(),
        isLoggedIn = isLoggedInSelector(state);

  if (!isLoggedIn) {
    return Promise.resolve();
  }

  if (isFetchRequestedByUser) {
    bugsnagClient.leaveBreadcrumb('User initiated sync');
    dispatch(forceSync());
  }

  dispatch({
    type: 'GET_ALL_TODOS_REQUESTED',
    payload: { isFetchRequestedByUser },
  });

  return lysterClient
    .get('/todos-current')
    .then(({ data }) => data.items)
    .then((todos) => {
      dispatch({ type: 'GET_ALL_TODOS_SUCCEEDED', payload: { todos } });
      return dispatch(scheduleNextTodoChange());
    })
    .catch((error) => {
      dispatch({ type: 'GET_ALL_TODOS_FAILED', payload: { error } });
    });
};

export const useGetAllTodos = () => useActionCreator(getAllTodos);

export const undoLastAction = (): ThunkAction => (dispatch) => {
  logEvent('todo_undo');
  dispatch({ type: 'UNDO_LAST_ACTION' });
  dispatch(scheduleNextTodoChange());
};

const determineIfVirtual = (id) => id && id.length > 24;

const determineIfNew = (id) => !id || determineIfVirtual(id);

const updateTodoInner = (props: TimelineItem): ThunkAction => (
  dispatch,
  getState,
) => {
  if (props.isCalendarEvent) {
    return null;
  }

  const isNew = determineIfNew(props.id),
        state = getState(),
        id = isNew ? ObjectID.generate() : props.id,
        timeOfChange = new Date(),
        changeId = ObjectID.generate(),
        status = isNew || props.status ? props.status || 'todo' : undefined,
        // we have a new recurring id
        recurring = props.recurring
          ? {
            ...props.recurring,
            id: props.recurring.id || ObjectID.generate(),
          }
          : undefined,
        Authorization = authorizationHeaderSelector(state),

        changeSet = omitUndefined({
          ...props,
          recurring,
          status,
          id,
          changeId,
          timeOfChange,
        }),

        object = serializeTodoItem(omit(
          ['isVirtual', 'userId', 'lastTimeOfChange', 'links'],
          {
            ...changeSet,
          },
        ));

  return dispatch({
    type: isNew
      ? 'ADD_TODO_REQUEST'
      : 'PATCH_TODO_REQUEST',
    payload: object,
    meta: {
      offline: {
        effect: {
          url: `/todos/${id}`,
          method: 'patch',
          data: omit(['id'], object),
          headers: Authorization ? { Authorization } : undefined,
        },
        commit: {
          type: isNew ? 'ADD_TODO_COMMIT' : 'PATCH_TODO_COMMIT',
          meta: { id, changeId },
        },
        rollback: {
          type: isNew ? 'ADD_TODO_ROLLBACK' : 'PATCH_TODO_ROLLBACK',
          meta: { id, changeId },
        },
      },
    },
  });
};

const setUndoCheckpoint = ({
  id,
  recurring,
  status,
  title,
  postponedUntil,
  manualSortOrder,
}: TodoItem,
isBatch = false) => (dispatch: Dispatch) => {
  if (Object.keys(omitUndefined({
    recurring,
    status,
    title,
    postponedUntil,
  })).length === 0) {
    return null;
  }

  const isNew = determineIfNew(id),
        isVirtual = determineIfVirtual(id),
        text = isNew && !isVirtual && !isBatch ? `${l.added} '${title}'`
          : status ? status === 'postponed' ? `${l.movedTo} ${dayjs(postponedUntil).calendar()}`
            : `${l.movedTo} '${l[`listname_${status}`]}'`
            : title
              ? `${l.updated} '${title}'`
              : recurring
                ? `${l.updatedScheduleTo} ${recurringTextDescription(
                  recurring.interval,
                  recurring.nextOccurrence,
                )}`
                : manualSortOrder ? l.changedOrder : '';

  return dispatch({ type: 'SET_UNDO_CHECKPOINT', payload: { text } });
};

const presentPostponeModal = () => {
  const componentPromise = Manager.presentRegistered('PostponeModal', {
    showDialog: true,
    onClosed: () => componentPromise.updateProps({ showDialog: false }),
    onSelectPostponed: () => componentPromise.updateProps({ showDialog: false }),
  });
  return componentPromise;
};

export const updateTodoBatch = (items: Array<TodoItem>, props: TodoItem): ThunkAction => async (
  dispatch: Dispatch,
) => {
  const postponedUntil = await (props.status === 'postponed'
    ? presentPostponeModal()
    : Promise.resolve());

  const modifiedProps = props.status === 'postponed'
    ? { ...props, postponedUntil }
    : props;

  if (postponedUntil) {
    dispatch(updateSetting({ lastPostponedDate: dayjs(postponedUntil).format() }));
  }

  logEvent('todo_updated_batch', {
    ...props.status
      ? { status: props.status }
      : {},
    itemCount: items.length,
  });
  batch(() => {
    dispatch(setUndoCheckpoint(modifiedProps, true));
    items.map(({ id, ...template }, index) => dispatch(updateTodoInner({
      id,
      ...determineIfVirtual(id) ? template : {},
      ...modifiedProps,
      ...props.manualSortOrder ? { manualSortOrder: props.manualSortOrder - index } : {},
    })));
    dispatch(scheduleNextTodoChange());
  });
};

export const updateTodo = (
  { id, ...previous }: TodoItem,
  props: TodoItem,
): ThunkAction => async (dispatch) => {
  const postponedUntil = await (props.status === 'postponed' && !props.postponedUntil
    ? presentPostponeModal()
    : Promise.resolve());

  const modifiedProps = props.status === 'postponed' && !props.postponedUntil
    ? { ...props, postponedUntil }
    : props;

  const withTemplateData = determineIfNew(id)
    ? { ...previous, ...modifiedProps }
    : { id, ...modifiedProps };

  if (postponedUntil) {
    dispatch(updateSetting({ lastPostponedDate: dayjs(postponedUntil).format() }));
  }

  logEvent('todo_updated', props.status ? { status: props.status } : {});
  dispatch(setUndoCheckpoint(withTemplateData));
  dispatch(updateTodoInner(withTemplateData));
  dispatch(scheduleNextTodoChange());
};

export const createTodo = (props: TodoItem) => updateTodo({}, props);
